import { ColDef, GridApi, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import React, { useEffect, useState } from 'react';
import { LoadReports } from '../../actions';
import { ReportsModel } from '../../models/reports.model';
import { UtilityService } from '../../services/UtilityService';
import { emptyLabels, pageTitles } from '../shared/constants';
import GridComponent from '../shared/grid';
import { MessageTagRenderer } from '../shared/grid/msgTagRendrer';
import { TypeRenderer } from '../shared/grid/typeRendrer';
import PageHeaderComponent from '../shared/page-header.component';
import { ReportsViewComponent } from "./reports-view-component";
import { setHeaderCount, setHeaderTitle, setRefreshHandler, setSelectedCount } from "../../helpers/header.helper";
import { Contact } from "../../models/conversations";
import PullToRefresh from "react-simple-pull-to-refresh";
import { ToFieldRender } from "../shared/grid/toFiledRender";
import { HeaderTemplate, dateComparator } from "../shared/grid/grid.services";
import { customStringComparator } from "../../helpers/custom-comparators";
import { ReportsGroupGridIcon, ReportsScheduledGridIcon, ReportsScheduledGroupGridIcon, ViewReportIcon } from "../../icon";
import { CustomFilterCategory } from "../shared/filters/filter.model";
import FilterComponent from "../shared/filters/filter.component";
import _ from 'lodash';
import { Popover } from "@mui/material";
import * as selector from '../../selectors/selectors'
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useLocation } from "react-router";

interface IProps {
}

const ReportsComponent: React.FC<IProps> = (props: IProps) => {
    const dispatch = useDispatch()
    const Reports: ReportsModel[] = useSelector(selector.reports)
    const Contacts: Contact[] = useSelector(selector.contacts)
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const IsTeamsApp: boolean = useSelector(selector.isTeamsApp)
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [PageSize, setPageSize] = useState<number>(10000);
    const [showViewReport, setShowViewReport] = useState<boolean>(false);
    const [selectedRowData, setSelectedRowData] = useState<ReportsModel>()
    const [columnApi, setColumnApi] = useState<any>(null);
    const [anchorEl, SetAnchorEl] = useState<Element | null>(null)
    const [ShowFilterOptions, SetShowFilterOptions] = useState<boolean>(false)
    const [filterOptions, setFilterOptions] = useState<CustomFilterCategory[]>([]);
    const [selectedFilterTypes, setSelectedFilterTypes] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(0)
    const location = useLocation();

    const ReportsColoumnDefs: ColDef[] = [
        {
            field: "Contacts",
            headerName: "To",
            valueGetter: (params: ValueGetterParams) => {
                return params.data.Groups[0]?.GroupName || params.data.Campaign?.Name || getNameString(params)
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                const groupContactCount = params.data.Groups[0]?.Contacts
                const groupName = params.data.Groups[0]?.GroupName
                const contactName = getNameString(params)
                const campaignName = params.data.Campaign?.Name
                if (params.data) {
                    const To = {
                        component: ToFieldRender,
                        params: {
                            Count: groupContactCount, GroupName: groupName, Label: contactName, campaignName: campaignName
                        }
                    }
                    return To
                }
                return undefined
            },
        },
        {
            field: "Type",
            headerName: "Type",
            headerComponentParams: {
                template: selectedFilterTypes.includes("Type") ? `${HeaderTemplate.filterApplied(true)}` : ""
            },
            valueGetter: (params: ValueGetterParams) => { return params.data.Type === 1 ? "Broadcast" : params.data.Groups.length === 0 && params.data.TotalContacts === 1 ? "Scheduled" : "Scheduled Broadcast" },
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data) {
                    const label = params.data.Type === 1 ? "Broadcast" : params.data.Groups.length === 0 && params.data.TotalContacts === 1 ? "Scheduled" : "Scheduled Broadcast";
                    const icon = params.data.Type === 1 ? ReportsGroupGridIcon : params.data.Groups.length === 0 && params.data.TotalContacts === 1 ? ReportsScheduledGridIcon : ReportsScheduledGroupGridIcon;
                    const Status = {
                        component: TypeRenderer,
                        params: {
                            Icon: icon, Label: label, Id: params.data.ID
                        }
                    }
                    return Status
                }
                return undefined
            },
        },
        {
            field: "Message",
            minWidth: IsMobileDevice ? 2 : 450,
            headerName: "Message",
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data) {
                    const Message = {
                        component: MessageTagRenderer,
                        params: {
                            Message: params.data.Message, ShowMediaIcon: !!params.data.Media
                        }
                    }
                    return Message
                }
                return undefined
            },
            comparator: customStringComparator,
            autoHeight: true,
            hide: IsMobileDevice,
        },
        {
            field: "Sent",
            headerName: "Sent",
            valueFormatter: (params: ValueFormatterParams) => {
                return UtilityService.convertDateToDay(params.value)
            },
            valueGetter: (params: ValueGetterParams) => {
                return UtilityService.convertDateToDay(params.data.Sent)
            },
            comparator: (valueA, valueB, nodeA, nodeB) => dateComparator(nodeA.data.Sent, nodeB.data.Sent),
            sort: 'desc',
        },
        {
            field: "",
            lockPinned: true,
            maxWidth: 65,
            resizable: false,
            lockPosition: "right",
            cellClass: "ag-cell-actions-style c-pointer",
            headerClass: "bg-color-secondary",
            pinned: "right",
            hide: IsMobileDevice,
            cellRenderer: (params: ICellRendererParams) =>
                <ViewReportIcon
                    onClick={(e: any) => {
                        openViewReport(params.data as ReportsModel);
                    }}
                />
        },
    ]

    const initialFilterOptions = () => {
        //Scheduled Broadcast
        //Broadcast
        //Scheduled
        const filterData: any[] = [
            {
                Id: 1,
                Title: "Type",
                IsSelectedAll: false,
                Options: _(Reports)
                    .map(row => ({
                        Key: "ReportType",
                        Id: row.ID,
                        Label: row.Type === 1 ? "Broadcast" : row.Groups.length === 0 && row.TotalContacts === 1 ? "Scheduled" : "Scheduled Broadcast",
                        Value: row.ReportType,
                        IsSelected: false
                    }))
                    .uniqBy('Label')
                    .value()
            },
        ]
        setFilterOptions(filterData)
    }

    const handleApplyFilters = (filters: CustomFilterCategory[]) => {
        setFilterOptions(filters);
        const { result } = UtilityService.getFilterTypes(filters);
        setSelectedFilterTypes(result)

    };

    useEffect(() => {
        initialFilterOptions()
    }, [Reports])

    useEffect(() => {
        if (location?.state?.auditData) {
            setSelectedRowData(location?.state?.auditData)
            setShowViewReport(true)
            setHeaderCount(0)
        }
    }, [location?.state?.auditData])

    const getGridTitle = () => {
        return Reports.length === 1 ? "1 Report" : Reports.length + " " + pageTitles.REPORTS
    }

    const openViewReport = (params) => {
        setCurrentPage(gridApi?.paginationGetCurrentPage())
        setSelectedRowData(params)
        setShowViewReport(true);
    }

    const closeViewReport = () => {
        setShowViewReport(false);
        setHeaderTitle(pageTitles.REPORTS);
        setRefreshHandler(gridRefresh);
    }
    const getNameString = (params) => {

        let reportName = "";
        if (!!params.data.Groups && params.data.Groups.length >= 1) {
            let count = params.data.TotalContacts - params.data.Groups[0].Contacts;
            reportName = `${count ? "+" : ""} ${count > 1 ? `${count} Others` : count === 1 ? `${count} other` : ""}`.trim();
        }
        else {
            let count = params.data.TotalContacts - 1;
            reportName = `${!!params.data.Contacts[0]?.ContactId !== null && (params.data.Contacts[0]?.Name && (params.data.Contacts[0]?.Name).trim()) ? params.data.Contacts[0]?.Name : UtilityService.maskPhoneNumberOnLength(params.data.Contacts[0]?.Number)} ${count ? "+" : ""} ${count > 1 ? `${count} Others` : count === 1 ? `${count} Other` : ""}`.trim();
        }
        return reportName
    }

    useEffect(() => {
        LoadReports(dispatch, 1, PageSize);
    }, [])

    useEffect(() => {
        setHeaderTitle(pageTitles.REPORTS);
        setHeaderCount(Reports?.length || 0)
        setRefreshHandler(gridRefresh);
        return setHeaderTitle
    }, [gridApi, columnApi])

    useEffect(() => {
        if (!showViewReport) {
            setHeaderCount(Reports?.length || 0)
            setSelectedCount(0)
        }
    }, [Reports])

    const onGridReady = (params) => {
        setColumnApi(params.columnApi)
        setGridApi((prevApi) => {
            params.api.paginationGoToPage(currentPage);
            return params.api;
        });
    }

    const searchReport = (e: any) => {
        gridApi.setQuickFilter(e.target.value);
    }

    const gridRefresh = () => {
        resetFilters()
        LoadReports(dispatch, 1, PageSize);
        if (!!columnApi) {
            columnApi.resetColumnState()
        }

        if (!!gridApi) {
            gridApi.refreshCells({ force: true })
        }
    }

    const handleCloseFilter = () => {
        SetAnchorEl(null);
        SetShowFilterOptions(false);
    }

    const resetFilters = () => {
        setFilterOptions([])
        setSelectedFilterTypes([])
        initialFilterOptions()
    }

    const handleClickFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
        SetAnchorEl(event.currentTarget);
    }

    const pullToRefresh = () => new Promise((resolve) => setTimeout(resolve, 15)).then(() => LoadReports(dispatch, 1, PageSize));

    return (
        <>
            {Boolean(anchorEl) && IsMobileDevice ? <FilterComponent
                Filters={filterOptions}
                HandleApplyFilters={handleApplyFilters}
                OnClose={handleCloseFilter}
            /> : !showViewReport ?
                <PullToRefresh isPullable={IsMobileDevice} pullingContent='' onRefresh={pullToRefresh} className=''>
                    <div className="d-flex flex-direction h-p-100 o-hidden justify-between">
                        <div className={`${IsMobileDevice && "py-1"}`}>
                            <PageHeaderComponent
                                isReport={true}
                                GridTitle={""}
                                onSearch={searchReport}
                                Count={Reports.length}
                                PlaceHolder={"Find a Report..."}
                                ShowFilterIcon={true}
                                HandleFilter={handleClickFilter}
                                IsFilterApplied={filterOptions.flatMap(x => x.Options.filter(x => x.IsSelected)).length}
                            />
                            {!IsMobileDevice && <div className="report-text color-conv-section-inactive my-1 px-3">
                                <div>Reports display delivery stats for group and scheduled messages. Reports typically appear within 10-20 minutes after message initiation.</div>
                            </div>
                            }
                        </div>
                        <GridComponent
                            ColumnDefs={ReportsColoumnDefs}
                            RowData={Reports}
                            OnGridReady={onGridReady}
                            IsPaginationEnabled={!IsMobileDevice}
                            OnGridRowClick={openViewReport}
                            EmptyLabel={emptyLabels.REPORTS}
                            LockVisible={IsMobileDevice}
                            filterOptions={filterOptions}
                            IsFilterPresent={true}
                            gridApi={gridApi}
                            ShowRowPointer={true}
                        />
                        <div className={`${IsMobileDevice && UtilityService.isIPhone(navigator.userAgent) && "mb-3"}`} />
                    </div>
                </PullToRefresh>
                :
                <ReportsViewComponent
                    CloseViewReport={closeViewReport}
                    Contacts={Contacts}
                    IsMobileDevice={IsMobileDevice}
                    Report={selectedRowData}
                    IsTeamsApp={IsTeamsApp} />
            }

            {Boolean(anchorEl) && !IsMobileDevice &&
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleCloseFilter}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    classes={{ paper: `min-w-40 ${ShowFilterOptions ? "min-h-40" : ""}` }}
                >
                    <FilterComponent
                        Filters={filterOptions}
                        HandleApplyFilters={handleApplyFilters}
                        OnClose={handleCloseFilter}
                    />
                </Popover>
            }
        </>
    )
}

export default ReportsComponent;
